/**
 * Amazon S3, Google Cloud Storage and Azure Blob Storage list prices, one row
 * per storage class, keyed by the catalogue's provider and request value
 * (`storageClasses.ts`). Read by the portal's /providers page, and meant for
 * the desktop's storage tier picker too (#762), so no two screens can quote
 * different numbers for the same class — the same arrangement as
 * `backblazePricing.ts`.
 *
 * Prices live here and not in the catalogue: the catalogue holds only facts
 * the engine acts on, and prices are not one of them (ADR 0007). The one
 * exception to keeping them out of `docs/storage-classes.md` is the retrieval
 * options below: they size the restore estimate that stands between a person
 * and a retrieval bill (#963), so each figure is cited there.
 *
 * Every figure is the provider's own on-demand list price at one reference
 * region, read on the date in `checked`:
 *
 * - Amazon S3, us-east-1: the AWS Price List for Amazon S3 (version
 *   20260928230416) and for S3 Glacier Deep Archive (20260911124507), and the
 *   AWS Data Transfer offer (20260916132208) for egress. Storage is the first
 *   50 TB tier; egress the first 10 TB beyond the free 100 GB.
 * - Google Cloud Storage, us-central1: https://cloud.google.com/storage/pricing
 *   — regional storage, retrieval fees, general network usage to worldwide
 *   destinations (excluding Asia and Australia) for the first 10 TiB, and the
 *   Always Free 100 GB from North America, which applies in us-central1.
 * - Azure Blob Storage, East US, LRS, general-purpose v2 block blobs: the
 *   Azure Retail Prices API (the figures
 *   https://azure.microsoft.com/pricing/details/storage/blobs/ renders), and
 *   https://azure.microsoft.com/pricing/details/bandwidth/ for egress over the
 *   Microsoft network after the free 100 GB. Archive's retrieval, at
 *   Standard priority, is the Retail Prices API's "Archive Data Retrieval"
 *   and "Archive Read Operations" meters of product General Block Blob v2 (a
 *   general-purpose v2 account without a hierarchical namespace),
 *   pay-as-you-go, read 2026-10-16; `docs/storage-classes.md` cites them
 *   (AZ-RP) under "Retrieval by Copy Blob".
 *
 * A class that waits on a retrieval is priced at its Standard retrieval
 * option (Azure: Standard priority), the one a restore would use unless asked
 * to hurry; its `timeToFirstByte` is that option's. S3 Glacier Flexible
 * Retrieval, S3 Glacier Deep Archive and Azure Archive also carry
 * `retrieval`: each retrieval option's fees and wait, read from the same
 * price lists and the S3 User Guide or Azure's rehydration page, and what the
 * retrieved copy is billed at while it is kept.
 *
 * Units follow the providers' own per-GB prices, at 1,000 GB to the TB —
 * Google bills by the GiB, and the table rounds that difference away. Treat
 * every row as the shape of the bill, not a quote.
 */

import type { StorageProvider } from "./storageClasses";

/** The three providers this table prices. */
export const HYPERSCALER_PROVIDERS = ["aws", "gcs", "azure"] as const satisfies readonly StorageProvider[];

export type HyperscalerProvider = (typeof HYPERSCALER_PROVIDERS)[number];

/**
 * How fast a provider retrieves an object whose reads wait on a retrieval:
 * Amazon S3's own names for the two it offers that Big Bear uses. Expedited
 * is left out on purpose (#963): Deep Archive does not have it, it is
 * unreliable on large packs without provisioned capacity, and it costs most.
 * Azure offers Standard alone: its High priority is left out (ADR 0026), and
 * it has nothing like Bulk.
 */
export const RETRIEVAL_OPTIONS = ["standard", "bulk"] as const;

export type RetrievalOption = (typeof RETRIEVAL_OPTIONS)[number];

/** What one retrieval option costs, and how long it waits. */
export interface RetrievalOptionPrice {
  /** Dollars per gigabyte retrieved. */
  perGb: number;
  /** Dollars per 1,000 retrieval requests, one request to an object. */
  perThousandRequests: number;
  /** How long a retrieval typically takes to finish, in words. */
  typicalWait: string;
  /**
   * The longest of that typical wait, in hours: what a restore waiting on
   * retrieval counts its expected finish from (#981). The same source as
   * `typicalWait`, so the two say the same wait.
   */
  typicalWaitHours: number;
}

/**
 * The retrieval options of a class whose reads wait on a retrieval: Standard
 * always, and Bulk where the provider has it (Amazon S3, not Azure).
 */
export interface RetrievalPrices {
  standard: RetrievalOptionPrice;
  bulk?: RetrievalOptionPrice;
  /**
   * Dollars per terabyte-month the retrieved temporary copy is billed at while
   * it is kept, on top of the archived object's own storage.
   */
  temporaryCopyPerTbMonth: number;
}

/** One class of one provider, at that provider's reference region. */
export interface StorageTierPrice {
  provider: HyperscalerProvider;
  /** The catalogue's request value for the class. */
  requestValue: string;
  /** Dollars per terabyte-month stored. */
  pricePerTbMonth: number;
  /** Dollars per gigabyte read back; 0 where reading is not charged by the gigabyte. */
  retrievalPerGb: number;
  /** Dollars per terabyte moved out to the internet, past the free allowance. */
  egressPerTb: number;
  /** Gigabytes a month moved out to the internet free of charge. */
  freeEgressGbPerMonth: number;
  /** How long a read waits before the first byte, in words. */
  timeToFirstByte: string;
  /**
   * Each retrieval option and the temporary copy's rate, on a class whose
   * reads wait on a retrieval and whose options are cited. Absent elsewhere.
   */
  retrieval?: RetrievalPrices;
  /** The region these prices are for; other regions differ. */
  referenceRegion: string;
  /** The month the row was checked against the provider's own prices. */
  checked: string;
  /** The provider's own pricing page. */
  pricingPage: string;
}

/** What every row of one provider shares: its egress, region, date and page. */
export type HyperscalerProviderPricing = Pick<
  StorageTierPrice,
  "provider" | "egressPerTb" | "freeEgressGbPerMonth" | "referenceRegion" | "checked" | "pricingPage"
>;

const CHECKED = "October 2026";

/** Each provider's shared facts, which every one of its rows below carries. */
export const HYPERSCALER_PROVIDER_PRICING: { readonly [P in HyperscalerProvider]: HyperscalerProviderPricing & { provider: P } } = {
  aws: {
    provider: "aws",
    egressPerTb: 90,
    freeEgressGbPerMonth: 100,
    referenceRegion: "us-east-1",
    checked: CHECKED,
    pricingPage: "https://aws.amazon.com/s3/pricing/",
  },
  gcs: {
    provider: "gcs",
    egressPerTb: 120,
    freeEgressGbPerMonth: 100,
    referenceRegion: "us-central1",
    checked: CHECKED,
    pricingPage: "https://cloud.google.com/storage/pricing",
  },
  azure: {
    provider: "azure",
    egressPerTb: 87,
    freeEgressGbPerMonth: 100,
    referenceRegion: "East US, LRS",
    checked: CHECKED,
    pricingPage: "https://azure.microsoft.com/pricing/details/storage/blobs/",
  },
};

const AWS = HYPERSCALER_PROVIDER_PRICING.aws;
const GCS = HYPERSCALER_PROVIDER_PRICING.gcs;
const AZURE = HYPERSCALER_PROVIDER_PRICING.azure;

const MILLISECONDS = "Milliseconds";

/**
 * The retrieved temporary copy, on both Glacier classes: S3 Standard's first
 * 50 TB rate, which in us-east-1 is lower than Reduced Redundancy's at every
 * band, so Amazon bills the copy as Standard.
 */
const S3_TEMPORARY_COPY_PER_TB_MONTH = 23;

const GLACIER_RETRIEVAL: RetrievalPrices = {
  standard: { perGb: 0.01, perThousandRequests: 0.05, typicalWait: "3–5 hours", typicalWaitHours: 5 },
  bulk: { perGb: 0, perThousandRequests: 0, typicalWait: "5–12 hours", typicalWaitHours: 12 },
  temporaryCopyPerTbMonth: S3_TEMPORARY_COPY_PER_TB_MONTH,
};

/** Azure Hot, LRS, the first 50 TB: what Hot vaults store at, and what an
 * Archive pack's retrieved copy is billed at while it is kept (ADR 0026). */
const AZURE_HOT_PER_TB_MONTH = 20.8;

/**
 * Azure Archive, at Standard priority: $0.02 per GB retrieved and $5.00 per
 * 10,000 reads, one read to a pack, and a retrieved copy on Hot for as long
 * as it is kept. "Up to 15 hours" is Azure's own figure for Standard (AZ-R).
 */
const AZURE_ARCHIVE_RETRIEVAL: RetrievalPrices = {
  standard: { perGb: 0.02, perThousandRequests: 0.5, typicalWait: "Up to 15 hours", typicalWaitHours: 15 },
  temporaryCopyPerTbMonth: AZURE_HOT_PER_TB_MONTH,
};

const DEEP_ARCHIVE_RETRIEVAL: RetrievalPrices = {
  standard: { perGb: 0.02, perThousandRequests: 0.1, typicalWait: "Up to 12 hours", typicalWaitHours: 12 },
  bulk: { perGb: 0.0025, perThousandRequests: 0.025, typicalWait: "Up to 48 hours", typicalWaitHours: 48 },
  temporaryCopyPerTbMonth: S3_TEMPORARY_COPY_PER_TB_MONTH,
};

/** A delayed row's Standard figures, read from its retrieval options so the two can't differ. */
const atStandard = (retrieval: RetrievalPrices) => ({
  retrievalPerGb: retrieval.standard.perGb,
  timeToFirstByte: retrieval.standard.typicalWait,
  retrieval,
});

/** Every row, in the catalogue's order. */
export const HYPERSCALER_PRICES: readonly StorageTierPrice[] = [
  // -- Amazon S3 ------------------------------------------------------------
  { ...AWS, requestValue: "STANDARD", pricePerTbMonth: 23, retrievalPerGb: 0, timeToFirstByte: MILLISECONDS },
  { ...AWS, requestValue: "STANDARD_IA", pricePerTbMonth: 12.5, retrievalPerGb: 0.01, timeToFirstByte: MILLISECONDS },
  { ...AWS, requestValue: "ONEZONE_IA", pricePerTbMonth: 10, retrievalPerGb: 0.01, timeToFirstByte: MILLISECONDS },
  { ...AWS, requestValue: "GLACIER_IR", pricePerTbMonth: 4, retrievalPerGb: 0.03, timeToFirstByte: MILLISECONDS },
  { ...AWS, requestValue: "GLACIER", pricePerTbMonth: 3.6, ...atStandard(GLACIER_RETRIEVAL) },
  { ...AWS, requestValue: "DEEP_ARCHIVE", pricePerTbMonth: 0.99, ...atStandard(DEEP_ARCHIVE_RETRIEVAL) },
  // -- Google Cloud Storage -------------------------------------------------
  { ...GCS, requestValue: "STANDARD", pricePerTbMonth: 20, retrievalPerGb: 0, timeToFirstByte: MILLISECONDS },
  { ...GCS, requestValue: "NEARLINE", pricePerTbMonth: 10, retrievalPerGb: 0.01, timeToFirstByte: MILLISECONDS },
  { ...GCS, requestValue: "COLDLINE", pricePerTbMonth: 4, retrievalPerGb: 0.02, timeToFirstByte: MILLISECONDS },
  { ...GCS, requestValue: "ARCHIVE", pricePerTbMonth: 1.2, retrievalPerGb: 0.05, timeToFirstByte: MILLISECONDS },
  // -- Azure Blob Storage ---------------------------------------------------
  { ...AZURE, requestValue: "Hot", pricePerTbMonth: AZURE_HOT_PER_TB_MONTH, retrievalPerGb: 0, timeToFirstByte: MILLISECONDS },
  { ...AZURE, requestValue: "Cool", pricePerTbMonth: 15.2, retrievalPerGb: 0.01, timeToFirstByte: MILLISECONDS },
  { ...AZURE, requestValue: "Cold", pricePerTbMonth: 3.6, retrievalPerGb: 0.03, timeToFirstByte: MILLISECONDS },
  { ...AZURE, requestValue: "Archive", pricePerTbMonth: 0.99, ...atStandard(AZURE_ARCHIVE_RETRIEVAL) },
];

/** The row for a provider's class, by the catalogue's request value, or none. */
export function lookupStorageTierPrice(provider: HyperscalerProvider, requestValue: string | null): StorageTierPrice | null {
  return HYPERSCALER_PRICES.find((p) => p.provider === provider && p.requestValue === requestValue) ?? null;
}

/**
 * A dollar amount as the price tables print it: whole dollars bare ("$23"),
 * anything with cents to the cent ("$0.99", "$12.50") — so "$1.2" never
 * appears.
 */
export function formatDollars(dollars: number): string {
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}
